import { css } from "@emotion/react";
import { Flex, Group, Stack, Text } from "@mantine/core";
import { Dispatch, FC, SetStateAction, useEffect } from "react";
import { DefaultValues, SubmitHandler, UseFormReset } from "react-hook-form";
import { z } from "zod";
import { RigsService } from "../api/generated/RigsService";
import { TrackedSerialNumbersService } from "../api/generated/TrackedSerialNumbersService";
import { createForm } from "../forms";
import { AdditionalParams } from "../hooks/paged-data-table/types";
import { useOptions } from "../hooks/use-options";
import { mapNullToUndefined } from "../utils/zod";

// Filter state excludes partnerSiteId since it comes from URL params
type FullFilterState = AdditionalParams<
  typeof TrackedSerialNumbersService.getListing
>;
export type FilterState = Omit<FullFilterState, "partnerSiteId">;

export type ComponentState = {
  filter: FilterState;
  tableState?: Parameters<typeof TrackedSerialNumbersService.getListing>[0];
};

const filterSchema = z.object({
  rigId: mapNullToUndefined(z.number().optional()),
  serviceStatusFilter: mapNullToUndefined(z.string().optional()),
  productionDateFrom: mapNullToUndefined(z.date().optional()),
  productionDateTo: mapNullToUndefined(z.date().optional()),
  certificationStatus: mapNullToUndefined(z.string().optional()),
  lastServiceDateFrom: mapNullToUndefined(z.date().optional()),
  lastServiceDateTo: mapNullToUndefined(z.date().optional()),
  lastServiceType: mapNullToUndefined(z.string().optional()),
  nextCosFrom: mapNullToUndefined(z.date().optional()),
  nextCosTo: mapNullToUndefined(z.date().optional()),
  nextUecocFrom: mapNullToUndefined(z.date().optional()),
  nextUecocTo: mapNullToUndefined(z.date().optional()),
});

export type FilterSchema = z.TypeOf<typeof filterSchema>;

const Form = createForm<FilterSchema>();

// Status options matching new backend ServiceStatus values
const StatusOptions = {
  green: "Active",
  gray: "Inactive",
  yellow: "In Repair",
  red: "Scrapped",
} as const;

// Certification status options (health states)
const CertificationOptions = {
  WithinCertification: "Within Certification",
  CosOverdue: "COS Overdue",
  UecocOverdue: "UECOC Overdue",
} as const;

type TableFiltersProps = {
  defaultValues?: DefaultValues<FilterSchema>;
  clear: boolean;
  setClear: Dispatch<SetStateAction<boolean>>;
  onSubmit: SubmitHandler<FilterSchema>;
  partnerSiteId: number;
};

export const TableFilters: FC<TableFiltersProps> = ({
  clear,
  setClear,
  defaultValues,
  onSubmit,
  partnerSiteId,
}) => {
  return (
    <Form
      onSubmit={onSubmit}
      schema={filterSchema}
      defaultValues={defaultValues}
      css={formCss}
    >
      {({ reset }) => (
        <FormFields
          clear={clear}
          setClear={setClear}
          reset={reset}
          partnerSiteId={partnerSiteId}
        />
      )}
    </Form>
  );
};

type FormFieldsProps = {
  clear: boolean;
  setClear: Dispatch<SetStateAction<boolean>>;
  reset: UseFormReset<FilterSchema>;
  partnerSiteId: number;
};

const FormFields: FC<FormFieldsProps> = ({
  clear,
  setClear,
  reset,
  partnerSiteId,
}) => {
  const rigOptions =
    useOptions(() => RigsService.getOptions({ partnerSiteId }), [partnerSiteId])
      .value ?? [];

  useEffect(() => {
    reset();
    setClear(false);
  }, [clear, reset, setClear]);

  return (
    <Stack w="100%" gap="sm">
      {/* Service Status - full width */}
      <Form.Select
        name="serviceStatusFilter"
        label="Service Status"
        enumObject={StatusOptions}
        placeholder="All"
        clearable
      />

      {/* Rig - full width */}
      <Form.Select
        name="rigId"
        label="Rig"
        placeholder="All"
        data={rigOptions}
        clearable
        searchable
      />

      {/* Certification - full width */}
      <Form.Select
        name="certificationStatus"
        label="Certificate Status"
        enumObject={CertificationOptions}
        placeholder="All"
        clearable
      />

      {/* Service Type - full width */}
      <Form.TextInput
        name="lastServiceType"
        label="Last Service Type"
        placeholder="COS, UECOC..."
      />

      {/* Production Date Range */}
      <DateRangeRow
        label="Production Date"
        fromName="productionDateFrom"
        toName="productionDateTo"
      />

      {/* Last Service Date Range */}
      <DateRangeRow
        label="Last Service Date"
        fromName="lastServiceDateFrom"
        toName="lastServiceDateTo"
      />

      {/* Next COS Date Range */}
      <DateRangeRow
        label="Next COS"
        fromName="nextCosFrom"
        toName="nextCosTo"
      />

      {/* Next UECOC Date Range */}
      <DateRangeRow
        label="Next UECOC"
        fromName="nextUecocFrom"
        toName="nextUecocTo"
      />

      {/* Apply Button */}
      <Group ml="auto" mt="sm">
        <Form.Submit>Apply</Form.Submit>
      </Group>
    </Stack>
  );
};

// Reusable date range row component
type DateRangeRowProps = {
  label: string;
  fromName: keyof FilterSchema;
  toName: keyof FilterSchema;
};

const DateRangeRow: FC<DateRangeRowProps> = ({ label, fromName, toName }) => {
  return (
    <Stack gap={4}>
      <div className={"form-field"}>
        <label className={"form-field-label"}>{label}</label>
      </div>
      <Flex gap="xs" align="flex-end">
        <Group w="50%" grow>
          <Form.DatePickerInput
            name={fromName}
            label={<Text size="xs">From</Text>}
            placeholder="Select date"
            clearable
            style={{ flex: 1 }}
          />
        </Group>
        <Text mb={8}>&mdash;</Text>
        <Group w="50%" grow>
          <Form.DatePickerInput
            name={toName}
            label={<Text size="xs">To</Text>}
            placeholder="Select date"
            clearable
            style={{ flex: 1 }}
          />
        </Group>
      </Flex>
    </Stack>
  );
};

const formCss = css({
  "& > *": {
    marginTop: 8,
  },
});